import React from "react";
import { useEffect } from "react";
import { FaTimes } from "react-icons/fa";
import { useForm, Controller, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { API_BASE_URL } from "../../api/config";
import { useTranslateKey } from "../../components/translation/useTranslateKey";
import { getCookie } from "../../api/csrf";

const ContactUsModal = ({ show, onClose }) => {
  const t = useTranslateKey();
  const schema = Yup.object().shape({
    name: Yup.string()
      .required(t("error.requiredName"))
      .matches(/^[\p{L} ]+$/u, t("error.nameOnlyLetters")),
    phone: Yup.string()
      .required(t("error.requiredPhone"))
      .matches(/^[\d٠-٩]+$/, t("error.phoneDigitsOnly")) 
      .matches(/^[0-9٠-٩]{7,20}$/, t("error.invalidPhone")), 
    email: Yup.string()
      .nullable()
      .notRequired()
      .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, {
        message: t("error.invalidEmail"),
        excludeEmptyString: true,
      }),
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    try {
      const response = await fetch(`${API_BASE_URL}/contact_home/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCookie("csrftoken"),
        },
        credentials: "include", // Include cookies for CSRF token
        body: JSON.stringify(data),
      });

      const result = await response.json();
      console.log(result);

      onClose();
      reset();
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [show]);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 flex items-start justify-center z-50 pt-20 overflow-y-auto"
      style={{ backgroundColor: "rgba(18, 21, 37, 0.98)" }}
    >
      <div className="bg-[#11132F] border-2 border-[#332D7E] rounded-xl p-8 max-w-md w-full mx-4 relative shadow-2xl">
        <div className="flex justify-end mb-4">
          <button
            className="w-8 h-8 bg-[#F9C835] text-[#121525] rounded-full flex items-center justify-center hover:bg-[#332D7E] hover:text-white transition-all duration-300"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            <FaTimes className="text-2xl" />
          </button>
        </div>
        <h3 className="text-2xl font-bold text-white mb-6">{t("contactUs")}</h3>

        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="space-y-4"
        >
          {/* Name Field */}
          <div>
            <label className="block text-[#F9C835] mb-2">{t("name")} *</label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <input
                  type="text"
                  {...field}
                  onBlur={() => trigger("name")}
                  className="search-input w-full text-sm py-1.5 px-3 bg-transparent border border-[#332D7E] rounded-lg focus:border-[#F9C835] text-white"
                />
              )}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1 font-medium p-1 rounded-md shadow-sm">
                {errors.name.message}
              </p>
            )}
          </div>

          {/* Phone Field */}
          <div>
            <label className="block text-[#F9C835] mb-2">
              {t("mobileNumber")} *
            </label>

            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <input
                  type="tel"
                  {...field}
                  onBlur={() => trigger("phone")}
                  className="search-input w-full text-sm py-1.5 px-3 bg-transparent border border-[#332D7E] rounded-lg focus:border-[#F9C835] text-white"
                />
              )}
            />
            {errors.phone && (
              <p className="text-red-500 text-sm mt-1 font-medium p-1 rounded-md shadow-sm">
                {errors.phone.message}
              </p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label className="block text-[#F9C835] mb-2">{t("email")}</label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <input
                  type="email"
                  {...field}
                  onBlur={() => trigger("email")}
                  className="search-input w-full text-sm py-1.5 px-3 bg-transparent border border-[#332D7E] rounded-lg focus:border-[#F9C835] text-white"
                />
              )}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1 font-medium p-1 rounded-md shadow-sm">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Message Field */}
          <div>
            <label className="block text-[#F9C835] mb-2">{t("message")}</label>
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <textarea
                  {...field}
                  onBlur={() => trigger("message")}
                  className="search-input w-full text-sm py-1.5 px-3 bg-transparent border border-[#332D7E] rounded-lg focus:border-[#F9C835] text-white h-32"
                />
              )}
            />
            {errors.message && (
              <p className="text-red-500 text-sm mt-1 font-medium p-1 rounded-md shadow-sm">
                {errors.message.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-[#332D7E] hover:bg-[#F9C835] text-white hover:text-[#121525] px-4 py-3 rounded-lg transition-all duration-300 font-semibold"
          >
            {t("sendMessage")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactUsModal;
